import React from "react";
import type { Message } from "../types";
import { GBP_REQUEST, readPlan } from "../utils/gbpPlan";

/**
 * SGL-421: while the owner talks with the agent about the Business Profile card,
 * the card can sit far above. One button, docked above the composer, takes them
 * back to it. It shows only during that conversation: not when the card first
 * opens, and not when the chat has moved on to something else.
 */

/** Words that name a part of the card. */
const CARD_TOPICS =
  /\b(description|phone|opening\s+hours|hours|categor(y|ies)|services?|photos?|website|service\s+area|address|attributes?|approve\s+all)\b/i;
/** The agent speaking about the card (its prompt says "on the card"). */
const AGENT_ON_CARD = /\b(the|your)\s+(business\s+profile\s+)?card\b/i;
/** A short reply to a card question: "yes", "skip it", "send it". */
const SHORT_REPLY_WORDS = 6;

function cardTerms(message: Message): string[] {
  const plan = readPlan(message.gbpProposal?.plan);
  const terms: string[] = [];
  for (const row of plan?.rows ?? []) {
    for (const v of [row.display, row.value]) {
      const list = Array.isArray(v) ? v : [v];
      for (const x of list) {
        const text = typeof x === "string" ? x : x && typeof x === "object" && "name" in x ? String((x as { name: unknown }).name) : "";
        if (text.trim().length >= 4 && text.length <= 80) terms.push(text.trim().toLowerCase());
      }
    }
  }
  return terms;
}

function mentionsCard(text: string, terms: string[]): boolean {
  const t = text.toLowerCase();
  return GBP_REQUEST.test(text) || CARD_TOPICS.test(text) || terms.some((term) => t.includes(term));
}

/** The message holding the Business Profile card the latest exchange is about,
 *  or -1 when the button should not show. */
export function backToCardIndex(messages: Message[], isLoading: boolean): number {
  if (isLoading || messages.length < 3) return -1;
  const last = messages.length - 1;
  if (messages[last].role !== "assistant") return -1;

  let cardAt = -1;
  for (let i = last - 1; i >= 0; i--) {
    const card = messages[i].gbpProposal;
    if (card && (card.plan !== undefined || card.planError)) {
      cardAt = i;
      break;
    }
  }
  if (cardAt < 0) return -1;

  // The owner's latest message, which must come after the card.
  let userAt = -1;
  for (let i = last - 1; i > cardAt; i--) {
    if (messages[i].role === "user") {
      userAt = i;
      break;
    }
  }
  if (userAt < 0) return -1;

  const terms = cardTerms(messages[cardAt]);
  const asked = messages[userAt].content || "";
  const answer = messages[last].content || "";
  if (mentionsCard(asked, terms) || AGENT_ON_CARD.test(answer)) return cardAt;

  // "yes" / "skip it" right after the agent asked about the card.
  const before = messages.slice(cardAt, userAt).reverse().find((m) => m.role === "assistant");
  const short = asked.trim().split(/\s+/).length <= SHORT_REPLY_WORDS;
  if (short && before && (before === messages[cardAt] || AGENT_ON_CARD.test(before.content || ""))) return cardAt;
  return -1;
}

const reducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Scrolls to the card itself (not the reply text above it) and moves focus there. */
export function scrollToCard(index: number): void {
  const wrapper = document.querySelector<HTMLElement>(`[data-message-index="${index}"]`);
  if (!wrapper) return;
  const card = wrapper.querySelector<HTMLElement>('section[aria-label$="Business Profile proposal"]') ?? wrapper;
  card.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "start" });
  if (!card.hasAttribute("tabindex")) card.setAttribute("tabindex", "-1");
  card.focus({ preventScroll: true });
}

/** Docked just above the composer, lined up with its left edge. */
export const GbpBackToCard: React.FC<{ index: number }> = ({ index }) => (
  <div className="flex px-1 pb-2 motion-safe:animate-statusIn">
    <button
      type="button"
      onClick={() => scrollToCard(index)}
      className="group inline-flex h-8 items-center gap-1.5 rounded-full border border-theme-border bg-theme-surface/90 px-3.5 text-[13px] font-medium text-theme-fg shadow-[0_1px_2px_rgba(16,24,40,0.05),0_6px_16px_-10px_rgba(16,24,40,0.25)] backdrop-blur-sm transition-colors hover:border-theme-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-fg-muted"
    >
      <span aria-hidden="true" className="text-theme-fg-muted transition-transform group-hover:-translate-y-0.5">↑</span>
      Back to your Business Profile card
    </button>
  </div>
);
